import { useCallback, useEffect, useRef, useState } from "react"

function csrfToken() {
  if (typeof document === "undefined") return ""
  const el = document.querySelector('meta[name="csrf-token"]')
  return el?.getAttribute("content") || ""
}

// Wraps POST /app/transcriptions. Returns { state, error, text, transcribe, reset }.
//
// An in-flight upload is aborted automatically if the component holding
// the hook unmounts, the caller calls `reset()`, or a subsequent
// `transcribe()` is fired. Without that, a user who navigates away
// mid-upload leaves a fetch running that still consumes the
// per-account quota and — if it resolves — calls setState on a dead
// component.
export function useTranscription() {
  const [state, setState] = useState("idle") // 'idle' | 'uploading' | 'done' | 'error'
  const [text, setText] = useState("")
  const [error, setError] = useState(null)

  const abortRef = useRef(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const safeSetState = (next) => {
    if (isMountedRef.current) setState(next)
  }
  const safeSetText = (next) => {
    if (isMountedRef.current) setText(next)
  }
  const safeSetError = (next) => {
    if (isMountedRef.current) setError(next)
  }

  const transcribe = useCallback(async ({ blob, mimeType, durationMs }) => {
    // Cancel any prior upload so the newest call wins.
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    safeSetError(null)
    safeSetState("uploading")
    try {
      const form = new FormData()
      const ext = (mimeType || "audio/webm").split("/")[1].split(";")[0]
      form.append("audio", blob, `clip.${ext}`)
      form.append("duration_ms", String(Math.round(durationMs || 0)))

      const response = await fetch("/app/transcriptions", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        body: form,
        signal: controller.signal,
      })

      if (response.status === 429) {
        const retry = response.headers.get("Retry-After")
        throw new Error(
          `Daily voice limit reached. Keep typing, or try again in ${retry}s.`,
        )
      }
      if (!response.ok) {
        const body = await response
          .json()
          .catch(() => ({ error: "transcription failed" }))
        throw new Error(body.error || "transcription failed")
      }

      const data = await response.json()
      safeSetText(data.text || "")
      safeSetState("done")
      return data.text
    } catch (err) {
      // A user-cancelled or unmount-cancelled fetch isn't an error worth
      // surfacing — the caller already knows the flow ended.
      if (err?.name === "AbortError") return null
      safeSetError(err.message || "Couldn't reach transcription service.")
      safeSetState("error")
      return null
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    safeSetState("idle")
    safeSetText("")
    safeSetError(null)
  }, [])

  return { state, text, error, transcribe, reset }
}
